import { RNWebViewChild } from "@crossmint/client-sdk-rn-window";
import {
	ChildWindow,
	type HandshakeChild,
	type HandshakeOptions,
} from "@crossmint/client-sdk-window";
import {
	exportSignerInboundEvents,
	exportSignerOutboundEvents,
	signerInboundEvents,
	signerOutboundEvents,
} from "@crossmint/client-signers";
import { CrossmintFrameService } from "../service";

export class EventsService extends CrossmintFrameService {
	name = "Events Service";
	log_prefix = "[EventsService]";
	private static messenger: HandshakeChild<
		typeof signerInboundEvents,
		typeof signerOutboundEvents
	> | null = null;
	private static exportMessenger: HandshakeChild<
		typeof exportSignerInboundEvents,
		typeof exportSignerOutboundEvents
	> | null = null;

	/**
	 * Initialize the messenger and register event handlers
	 * The messenger is shared across all instances
	 */
	async init(options?: {
		handshakeOptions?: HandshakeOptions;
		targetOrigin?: string;
	}): Promise<void> {
		if (EventsService.messenger) {
			this.logger.debug(this.log_prefix, "Messenger already initialized");
			return;
		}

		// Both Android and iOS native clients, not just RN clients, populate `window.ReactNativeWebView`
		if ("ReactNativeWebView" in window && window.ReactNativeWebView != null) {
			this.logger.info(this.log_prefix, "Initializing RN WebView messenger");
			EventsService.messenger = new RNWebViewChild({
				incomingEvents: signerInboundEvents,
				outgoingEvents: signerOutboundEvents,
			});
		} else {
			const urlParams = new URLSearchParams(window.location.search);
			const targetOrigin = urlParams.get("targetOrigin");
			if (targetOrigin == null) {
				throw new Error("targetOrigin is required");
			}

			this.logger.info(
				this.log_prefix,
				"Initializing iframe messenger",
				targetOrigin,
			);
			EventsService.messenger = new ChildWindow(window.parent, targetOrigin, {
				incomingEvents: signerInboundEvents,
				outgoingEvents: signerOutboundEvents,
				handshakeOptions: options?.handshakeOptions,
			});
		}
	}

	/**
	 * Initialize export messenger for export events
	 */
	async initExportMessenger(options?: {
		handshakeOptions?: HandshakeOptions;
		targetOrigin?: string;
	}): Promise<void> {
		if (EventsService.exportMessenger) {
			this.logger.debug(
				this.log_prefix,
				"Export messenger already initialized",
			);
			return;
		}

		if ("ReactNativeWebView" in window && window.ReactNativeWebView != null) {
			EventsService.exportMessenger = new RNWebViewChild({
				incomingEvents: exportSignerInboundEvents,
				outgoingEvents: exportSignerOutboundEvents,
			});
		} else {
			const urlParams = new URLSearchParams(window.location.search);
			const targetOrigin = urlParams.get("targetOrigin");
			if (targetOrigin == null) {
				throw new Error("targetOrigin is required");
			}

			EventsService.exportMessenger = new ChildWindow(
				window.parent,
				targetOrigin,
				{
					incomingEvents: exportSignerInboundEvents,
					outgoingEvents: exportSignerOutboundEvents,
					handshakeOptions: options?.handshakeOptions,
				},
			);
		}

		await EventsService.exportMessenger?.handshakeWithParent();
	}

	/**
	 * Assert that the messenger is initialized and connected
	 */
	private assertMessengerInitialized(): void {
		if (!EventsService.messenger) {
			throw new Error("Messenger not initialized");
		}
		if (!EventsService.messenger.isConnected) {
			throw new Error("Messenger not connected");
		}
	}

	/**
	 * Assert that the export messenger is initialized and connected
	 */
	private assertExportMessengerInitialized(): void {
		if (!EventsService.exportMessenger) {
			throw new Error("Export messenger not initialized");
		}
		if (!EventsService.exportMessenger.isConnected) {
			throw new Error("Export messenger not connected");
		}
	}

	async notifyReady() {
		if ("ReactNativeWebView" in window && window.ReactNativeWebView != null) {
			this.logger.info(this.log_prefix, "Sending frame-ready signal to parent");
			window.ReactNativeWebView.postMessage("frame-ready");
		}
		if (!EventsService.messenger) {
			throw new Error("Messenger not initialized");
		}
		this.logger.info(this.log_prefix, "Starting handshake with parent...");
		await EventsService.messenger.handshakeWithParent();
		this.logger.info(this.log_prefix, "Handshake with parent completed");
	}

	/**
	 * Get the messenger instance
	 */
	getMessenger(): NonNullable<typeof EventsService.messenger> {
		this.assertMessengerInitialized();
		return EventsService.messenger as NonNullable<
			typeof EventsService.messenger
		>;
	}

	/**
	 * Get the export messenger instance
	 */
	getExportMessenger(): NonNullable<typeof EventsService.exportMessenger> {
		this.assertExportMessengerInitialized();
		return EventsService.exportMessenger as NonNullable<
			typeof EventsService.exportMessenger
		>;
	}
}
